import { detectLanguages as detectLanguagesRequest } from '@src/repositories/languageDetection';
import { LogManager } from '@src/utility/Logger';

import { err, ok } from './common/Result';
import {
  autoMapColumns,
  buildPreview,
  type ColumnMapping,
  type ImportCounts,
  type ImportPreview,
  type ImportTarget,
} from './translationMemoryImport/importPreview';
import { parseSheet } from './translationMemoryImport/parseSheet';

import type { Result } from './common/Result';
import type { LanguageSampleSet } from '@src/repositories/languageDetection';
import type {
  EntryBulkWrite,
  TranslationMemoryEntry,
  TranslationMemoryEntryRepository,
} from '@src/repositories/TranslationMemoryEntryRepository';
import type {
  TranslationMemoryImport,
  TranslationMemoryImportRepository,
} from '@src/repositories/TranslationMemoryImportRepository';

const logger = LogManager.getLogger('service:TranslationMemoryImport');

/** Cells sampled per column for language detection (PLAT-10900). */
const DETECT_SAMPLE_SIZE = 25;

/** A detected language for one column: BCP-47 code + the model's justification. */
export interface DetectedLanguage {
  code: string;
  reason?: string;
}

type ImportRepositoryDeps = Pick<
  TranslationMemoryImportRepository,
  'fetchForShowcase' | 'uploadFile' | 'create' | 'updateCounts'
>;
type EntryRepositoryDeps = Pick<TranslationMemoryEntryRepository, 'writeMany'>;

/** Everything needed to write a previewed import. */
export interface CommitInput {
  showcaseId: string;
  file: File;
  preview: ImportPreview;
  target: ImportTarget;
  personId: string | null;
  /** Raw language strings from the file, for provenance (optional). */
  sourceLanguageRaw?: string | null;
  translationLanguageRaw?: string | null;
}

/** Result of a committed import: the record + the entries applied in place. */
export interface CommitResult {
  import: TranslationMemoryImport;
  created: TranslationMemoryEntry[];
  overwritten: TranslationMemoryEntry[];
  counts: ImportCounts;
}

/**
 * Orchestrates single-file document import (PLAT-10848). `buildPreview` parses +
 * validates + classifies rows against existing entries (pure, no writes);
 * `commit` uploads the file, writes the retained `translation_memory_import`
 * record, then hands the classified creates/overwrites to
 * `entryRepo.writeMany` for a bounded-concurrency bulk write + bulk publish
 * (`origin=import`, `import` link set — E11/E12). Result-returning with lazy
 * repository getters, mirroring `TranslationMemoryEntryService`.
 */
export class TranslationMemoryImportService {
  constructor(
    private readonly getImportRepository: () => ImportRepositoryDeps,
    private readonly getEntryRepository: () => EntryRepositoryDeps,
  ) {}

  fetchForShowcase(
    showcaseId: string,
  ): Promise<Result<TranslationMemoryImport[]>> {
    if (!showcaseId)
      return Promise.resolve(err('VALIDATION', 'showcaseId is required'));
    return this.runImport('load translation memory imports', repo =>
      repo.fetchForShowcase(showcaseId),
    );
  }

  /**
   * Read a file's header row + row count and suggest a column mapping (DS5), so
   * the configure step can show mapping dropdowns before a full preview. Throws
   * ⇒ EXTERNAL (unparseable file).
   */
  async inspectFile(file: File): Promise<
    Result<{
      headers: string[];
      rowCount: number;
      suggestedMapping: ColumnMapping;
    }>
  > {
    try {
      const sheet = await parseSheet(file);
      return ok({
        headers: sheet.headers,
        rowCount: sheet.rows.length,
        suggestedMapping: autoMapColumns(sheet.headers),
      });
    } catch (e) {
      logger.error('Failed to inspect import file', e);
      return err(
        'EXTERNAL',
        e instanceof Error ? e.message : 'Could not read the file.',
        e,
      );
    }
  }

  /**
   * Sample the first ~25 non-empty cells of the mapped source-term and
   * translation columns and detect their dominant language (PLAT-10900), so the
   * configure step can warn about a source/target language mismatch. Returns the
   * detected BCP-47 codes (null when undetermined or no column mapped).
   * Non-blocking: EXTERNAL on failure so the caller just skips the warning.
   * `signal` aborts the in-flight request — remapping a column supersedes the
   * previous detect, and this is a paid LLM call.
   */
  async detectLanguages(
    file: File,
    mapping: ColumnMapping,
    signal?: AbortSignal,
  ): Promise<
    Result<{
      source: DetectedLanguage | null;
      translation: DetectedLanguage | null;
    }>
  > {
    try {
      const sheet = await parseSheet(file);
      const sample = (col: number | null): string[] =>
        col == null || col < 0
          ? []
          : sheet.rows
              .map(row => row[col] ?? '')
              .filter(Boolean)
              .slice(0, DETECT_SAMPLE_SIZE);
      const sets: LanguageSampleSet[] = [];
      const sourceSamples = sample(mapping.sourceTerm);
      const translationSamples = sample(mapping.translation);
      if (sourceSamples.length)
        sets.push({ id: 'source', samples: sourceSamples });
      if (translationSamples.length)
        sets.push({ id: 'translation', samples: translationSamples });
      if (sets.length === 0) return ok({ source: null, translation: null });

      const results = await detectLanguagesRequest(sets, signal);
      const byId = new Map(results.map(r => [r.id, r]));
      const pick = (id: string): DetectedLanguage | null => {
        const r = byId.get(id);
        return r && r.language && r.language !== 'und'
          ? { code: r.language, reason: r.reason }
          : null;
      };
      return ok({ source: pick('source'), translation: pick('translation') });
    } catch (e) {
      logger.warn('Language detection failed', e);
      return err(
        'EXTERNAL',
        e instanceof Error ? e.message : 'Language detection failed.',
        e,
      );
    }
  }

  /** Parse + validate + classify the file against existing entries (no writes). */
  async buildPreview(
    file: File,
    mapping: ColumnMapping,
    target: ImportTarget,
    existingEntries: TranslationMemoryEntry[],
  ): Promise<Result<ImportPreview>> {
    if (!target.sourceLanguage?.id)
      return err('VALIDATION', 'source language is required');
    if (mapping.sourceTerm < 0)
      return err('VALIDATION', 'a source-term column must be mapped');
    try {
      const sheet = await parseSheet(file);
      return ok(buildPreview(sheet, mapping, target, existingEntries));
    } catch (e) {
      logger.error('Failed to parse import file', e);
      return err(
        'EXTERNAL',
        e instanceof Error ? e.message : 'Could not read the file.',
        e,
      );
    }
  }

  /**
   * Write a previewed import: upload the file, create the retained record, then
   * bulk-write the entry creates/overwrites. Rejects on validation before any
   * write.
   */
  async commit(input: CommitInput): Promise<Result<CommitResult>> {
    const { showcaseId, file, preview, target, personId } = input;
    if (!showcaseId) return err('VALIDATION', 'showcaseId is required');
    if (preview.fileErrors.length)
      return err('VALIDATION', 'file has unresolved structural errors');

    const writableRows = preview.rows.filter(r => !r.error);
    if (writableRows.every(r => r.outcomes.every(o => o.action === 'skipped')))
      return err('VALIDATION', 'nothing to import (all rows unchanged)');

    let importRepo: ImportRepositoryDeps;
    let entryRepo: EntryRepositoryDeps;
    try {
      importRepo = this.getImportRepository();
      entryRepo = this.getEntryRepository();
    } catch (e) {
      logger.error('Import repositories not initialized', e);
      return err(
        'PRECONDITION',
        e instanceof Error ? e.message : 'Repository not initialized',
        e,
      );
    }

    try {
      const uploadId = await importRepo.uploadFile(file);
      if (!uploadId) throw new Error('File upload returned null');

      const record = await importRepo.create(
        showcaseId,
        {
          uploadId,
          fileName: file.name,
          sourceLanguage: target.sourceLanguage,
          translationLanguage: target.translationLanguage,
          sourceLanguageRaw: input.sourceLanguageRaw,
          translationLanguageRaw: input.translationLanguageRaw,
          collectionIds: target.collectionIds,
          counts: preview.counts,
        },
        personId,
      );
      if (!record) throw new Error('Import record create returned null');

      const writes: EntryBulkWrite[] = [];
      let skipped = 0;
      for (const row of writableRows) {
        for (const outcome of row.outcomes) {
          if (outcome.action === 'skipped') {
            skipped += 1;
            continue;
          }
          writes.push({
            input: {
              sourceTerm: row.sourceTerm,
              sourceLanguage: target.sourceLanguage,
              translation: row.translation,
              translationLanguage: target.translationLanguage,
              comment: row.comment,
              collectionId: outcome.collectionId,
            },
            existingEntryId:
              outcome.action === 'overwritten'
                ? outcome.existingEntryId
                : undefined,
          });
        }
      }

      const { created, overwritten, failed } = await entryRepo.writeMany(
        showcaseId,
        writes,
        personId,
        { origin: 'import', importId: record.id },
      );
      const actual: ImportCounts = {
        created: created.length,
        overwritten: overwritten.length,
        skipped,
        failed,
      };

      // The record had to exist before the writes (entries link back to it), so
      // it was created with the preview's projected counts. Restamp it with what
      // actually happened. A failed restamp leaves a stale count on a successful
      // import — logged, never fatal, since the entries are already committed.
      if (!(await importRepo.updateCounts(record.id, actual))) {
        logger.warn('Import committed but its counts could not be updated', {
          importId: record.id,
        });
      }

      return ok({
        // Return the record carrying the real counts, not the create-time echo:
        // the store prepends this to the import history, which would otherwise
        // keep showing the preview's numbers until the next reload.
        import: { ...record, counts: actual },
        created,
        overwritten,
        counts: actual,
      });
    } catch (e) {
      logger.error('Failed to commit translation memory import', e);
      return err(
        'EXTERNAL',
        e instanceof Error ? e.message : 'Import failed.',
        e,
      );
    }
  }

  private async runImport<T>(
    description: string,
    fn: (repo: ImportRepositoryDeps) => Promise<T>,
  ): Promise<Result<T>> {
    let repository: ImportRepositoryDeps;
    try {
      repository = this.getImportRepository();
    } catch (e) {
      logger.error('Translation memory import repository not initialized', e);
      return err(
        'PRECONDITION',
        e instanceof Error ? e.message : 'Repository not initialized',
        e,
      );
    }
    try {
      return ok(await fn(repository));
    } catch (e) {
      logger.error(`Failed to ${description}`, e);
      return err(
        'EXTERNAL',
        e instanceof Error ? e.message : 'Unknown error',
        e,
      );
    }
  }
}
